/**
 * FERP Time & Attendance Hooks
 *
 * Wired to Payroll subgraph (PayrollTimeEntry / PayrollTimesheet) for time tracking.
 * Payroll is the authority — compliance-first (wf-5-0-006 state machine,
 * break rules, rounding, OT detection via Zen Engine).
 *
 * Session 3: Rewired from ERP → Payroll. No more field transforms needed —
 * Payroll types map 1:1 to frontend types (employeeId, status, hours native).
 *
 * APPEND-ONLY ARCHITECTURE:
 * - Clock in creates PayrollTimeEntry (status: clocked_in) + immutable PunchRecord
 * - Clock out transitions TimeEntry (clocked_in → clocked_out) + creates PunchRecord
 * - Break start/end = status transitions + PunchRecord (immutable audit)
 */
// @ts-nocheck

import { useAuditLogger } from '../repositories';
import { useCompatQuery, useCompatMutation } from './useApolloCompat';
import {
  TIMESHEETS_QUERY,
  CLOCK_IN_MUTATION,
  CLOCK_OUT_MUTATION,
  WHOS_IN_QUERY,
  TIME_ENTRIES_QUERY,
  SUBMIT_TIME_ENTRY_MUTATION,
  START_BREAK_MUTATION,
  END_BREAK_MUTATION,
  PAYROLL_SUBMIT_TIMESHEET_MUTATION,
  PAYROLL_APPROVE_TIMESHEET_MUTATION,
  PAYROLL_REJECT_TIMESHEET_MUTATION,
  EXCEPTIONS_QUERY,
} from '../graphql/operations';

// ── Payroll → Frontend field transform ─────────────────────────────
// Payroll types are 1:1 — minimal transform needed

function payrollTimesheetToTimesheet(ts: any) {
  return {
    id: ts.id,
    employeeId: ts.employeeId || '',
    startDate: ts.payPeriodStart || '',
    endDate: ts.payPeriodEnd || '',
    totalHours: (ts.totalRegularHours || 0) + (ts.totalOvertimeHours || 0) + (ts.totalDoubleTimeHours || 0),
    totalRegular: ts.totalRegularHours || 0,
    totalOvertime: ts.totalOvertimeHours || 0,
    totalDoubleTime: ts.totalDoubleTimeHours || 0,
    totalBreak: ts.totalBreakHours || 0,
    totalPto: ts.totalPtoHours || 0,
    isVerified: ts.status === 'APPROVED',
    status: ts.status,
    approvedBy: ts.approvedBy,
    approvedAt: ts.approvedAt,
    submittedAt: ts.submittedAt,
    rejectionReason: ts.rejectionReason,
    notes: ts.notes,
    days: [],
  };
}

function payrollWhosInToWhosIn(entry: any) {
  return {
    employeeId: entry.employeeId || '',
    employeeName: entry.employeeName || entry.employeeId || '',
    punchInTime: entry.punchInTime || '',
    timeEntryId: entry.timeEntryId || '',
    branchName: '',
    departmentName: entry.departmentId || '',
    source: entry.source,
  };
}

// ── Standard CRUD via Repository ────────────────────────────────────

export function useTimesheets(variables: { employeeId?: string; startDate: string; endDate: string }) {
  const result = useCompatQuery(TIMESHEETS_QUERY, {
    variables: {
      employeeId: variables.employeeId,
      payPeriodStart: variables.startDate,
      limit: 100,
    },
  });

  return {
    data: (result.data?.payrollTimesheets || []).map(payrollTimesheetToTimesheet),
    loading: result.loading,
    isLoading: result.loading,
    error: result.error,
    refetch: result.refetch,
  };
}

export function useTimeEntries(variables?: {
  employeeId?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  limit?: number;
}) {
  const result = useCompatQuery(TIME_ENTRIES_QUERY, {
    variables: {
      employeeId: variables?.employeeId,
      startDate: variables?.startDate,
      endDate: variables?.endDate,
      status: variables?.status,
      limit: variables?.limit || 100,
    },
  });

  return {
    data: result.data?.payrollTimeEntries || [],
    loading: result.loading,
    isLoading: result.loading,
    error: result.error,
    refetch: result.refetch,
  };
}

// ── Custom Mutations (Payroll TimeEntry wrappers) ───────────────────

/**
 * Clock in — creates PayrollTimeEntry (status: clocked_in) + PunchRecord.
 * Returns compat interface (.mutate, .isPending) for page compatibility.
 */
export function useClockIn() {
  const logger = useAuditLogger('TimePunch');

  const mutation = useCompatMutation(CLOCK_IN_MUTATION, {
    refetchQueries: [{ query: WHOS_IN_QUERY, variables: { limit: 200 } }],
  });

  const originalMutate = mutation.mutate;
  mutation.mutate = (variables?: any, callbacks?: any) => {
    logger.log('create', { action: 'clockIn' });
    const input = {
      employeeId: variables?.employeeId || '',
      source: variables?.source || 'WEB',
      note: variables?.note || undefined,
    };
    originalMutate({ input }, callbacks);
  };

  return mutation;
}

/**
 * Clock out — transitions PayrollTimeEntry (clocked_in → clocked_out).
 * Returns compat interface (.mutate, .isPending) for page compatibility.
 */
export function useClockOut() {
  const logger = useAuditLogger('TimePunch');

  const mutation = useCompatMutation(CLOCK_OUT_MUTATION, {
    refetchQueries: [{ query: WHOS_IN_QUERY, variables: { limit: 200 } }],
  });

  const originalMutate = mutation.mutate;
  mutation.mutate = (variables?: any, callbacks?: any) => {
    logger.log('create', { action: 'clockOut' });
    const input = {
      timeEntryId: variables?.timeEntryId || '',
      note: variables?.note || undefined,
    };
    originalMutate({ input }, callbacks);
  };

  return mutation;
}

/**
 * Start break — transitions clocked_in → on_break.
 */
export function useStartBreak() {
  const logger = useAuditLogger('TimePunch');

  const mutation = useCompatMutation(START_BREAK_MUTATION, {
    refetchQueries: [{ query: WHOS_IN_QUERY, variables: { limit: 200 } }],
  });

  const originalMutate = mutation.mutate;
  mutation.mutate = (variables?: any, callbacks?: any) => {
    logger.log('create', { action: 'startBreak' });
    originalMutate({ input: { timeEntryId: variables?.timeEntryId, note: variables?.note } }, callbacks);
  };

  return mutation;
}

/**
 * End break — transitions on_break → clocked_in.
 */
export function useEndBreak() {
  const logger = useAuditLogger('TimePunch');

  const mutation = useCompatMutation(END_BREAK_MUTATION, {
    refetchQueries: [{ query: WHOS_IN_QUERY, variables: { limit: 200 } }],
  });

  const originalMutate = mutation.mutate;
  mutation.mutate = (variables?: any, callbacks?: any) => {
    logger.log('create', { action: 'endBreak' });
    originalMutate({ input: { timeEntryId: variables?.timeEntryId, note: variables?.note } }, callbacks);
  };

  return mutation;
}

/**
 * Submit time entry — transitions clocked_out → submitted.
 */
export function useSubmitTimeEntry() {
  const logger = useAuditLogger('TimePunch');

  const mutation = useCompatMutation(SUBMIT_TIME_ENTRY_MUTATION);

  const originalMutate = mutation.mutate;
  mutation.mutate = (variables?: any, callbacks?: any) => {
    logger.log('create', { action: 'submitTimeEntry' });
    originalMutate({ id: variables?.id }, callbacks);
  };

  return mutation;
}

/**
 * Submit timesheet — transitions draft → submitted.
 */
export function useSubmitTimesheet() {
  const logger = useAuditLogger('Timesheet');
  const mutation = useCompatMutation(PAYROLL_SUBMIT_TIMESHEET_MUTATION);

  const originalMutate = mutation.mutate;
  mutation.mutate = (variables?: any, callbacks?: any) => {
    logger.log('create', { action: 'submitTimesheet' });
    originalMutate({ input: { timesheetId: variables?.timesheetId, notes: variables?.notes } }, callbacks);
  };

  return mutation;
}

/**
 * Approve timesheet — transitions submitted → approved.
 */
export function useApproveTimesheet() {
  const logger = useAuditLogger('Timesheet');
  const mutation = useCompatMutation(PAYROLL_APPROVE_TIMESHEET_MUTATION);

  const originalMutate = mutation.mutate;
  mutation.mutate = (variables?: any, callbacks?: any) => {
    logger.log('create', { action: 'approveTimesheet' });
    originalMutate({ input: { timesheetId: variables?.timesheetId, notes: variables?.notes } }, callbacks);
  };

  return mutation;
}

/**
 * Reject timesheet — transitions submitted → rejected.
 */
export function useRejectTimesheet() {
  const logger = useAuditLogger('Timesheet');
  const mutation = useCompatMutation(PAYROLL_REJECT_TIMESHEET_MUTATION);

  const originalMutate = mutation.mutate;
  mutation.mutate = (variables?: any, callbacks?: any) => {
    logger.log('create', { action: 'rejectTimesheet' });
    originalMutate({ input: { timesheetId: variables?.timesheetId, reason: variables?.reason } }, callbacks);
  };

  return mutation;
}

// ── Custom Queries (Payroll wrappers) ────────────────────────────────

/**
 * Who's in — queries payrollWhosIn (active entries, end_time null).
 * Polls every 30 seconds for real-time updates.
 */
export function useWhosIn() {
  const logger = useAuditLogger('TimePunch');

  const result = useCompatQuery(WHOS_IN_QUERY, {
    variables: { limit: 200 },
    pollInterval: 30000,
    onCompleted: () => logger.log('list', { type: 'whosIn' }),
  });

  const openEntries = (result.data?.payrollWhosIn || []).map(payrollWhosInToWhosIn);

  return {
    data: openEntries,
    loading: result.loading,
    isLoading: result.loading,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Get time exceptions requiring attention.
 * Uses repository-compatible interface with automatic audit logging.
 */
export function useExceptions(variables?: {
  page?: number;
  perPage?: number;
  startDate?: string;
  endDate?: string;
}) {
  const logger = useAuditLogger('TimeException');

  const result = useCompatQuery(EXCEPTIONS_QUERY, {
    variables,
    notifyOnNetworkStatusChange: true,
    onCompleted: () => logger.log('list', { type: 'exceptions', ...variables }),
  });

  return {
    data: result.data?.exceptions || [],
    loading: result.loading,
    isLoading: result.loading,
    error: result.error,
    refetch: result.refetch,
    networkStatus: result.networkStatus,
  };
}
